// src/pages/tenant/admin/AdminTenantsPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useTenantTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { Select } from '@/components/Select';

type TenantStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED' | 'DELETED';
type TenantType = 'PERSONAL' | 'TEAM' | 'ORGANIZATION' | 'ENTERPRISE' | 'DEDICATED';

const TENANT_STATUSES: TenantStatus[] = [
  'PENDING',
  'ACTIVE',
  'SUSPENDED',
  'ARCHIVED',
  'DELETED',
];

const TENANT_TYPES: TenantType[] = [
  'PERSONAL',
  'TEAM',
  'ORGANIZATION',
  'ENTERPRISE',
  'DEDICATED',
];

function mapTenantStatusToVariant(
  status: string,
): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (status) {
    case 'ACTIVE':
    case 'TENANT_STATUS_ACTIVE':
      return 'success';
    case 'PENDING':
    case 'TENANT_STATUS_PENDING':
      return 'warning';
    case 'SUSPENDED':
    case 'TENANT_STATUS_SUSPENDED':
      return 'error';
    case 'ARCHIVED':
    case 'TENANT_STATUS_ARCHIVED':
      return 'secondary';
    case 'DELETED':
    case 'TENANT_STATUS_DELETED':
      return 'error';
    default:
      return 'secondary';
  }
}

function mapTenantTypeToVariant(
  type: string,
): 'info' | 'primary' | 'warning' | 'secondary' | 'error' | 'success' {
  switch (type) {
    case 'PERSONAL':
    case 'TENANT_TYPE_PERSONAL':
      return 'info';
    case 'TEAM':
    case 'TENANT_TYPE_TEAM':
      return 'info';
    case 'ORGANIZATION':
    case 'TENANT_TYPE_ORGANIZATION':
      return 'primary';
    case 'ENTERPRISE':
    case 'TENANT_TYPE_ENTERPRISE':
      return 'warning';
    case 'DEDICATED':
    case 'TENANT_TYPE_DEDICATED':
      return 'warning';
    default:
      return 'secondary';
  }
}

function normalizeStatus(status: string): string {
  return status.replace('TENANT_STATUS_', '');
}

function normalizeType(type: string): string {
  return type.replace('TENANT_TYPE_', '');
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(dateStr);
  }
}

/**
 * Platform admin view of ALL tenants.
 * Route: /admin/tenants
 */
export const AdminTenantsPage = observer(() => {
  const store = useTenantTenant();
  const ops = useTenant();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [regionFilter, setRegionFilter] = useState(searchParams.get('region') || '');

  const loadTenants = useCallback(() => {
    const rules: any[] = [];
    const eq = (field: string, value: string) => ({
      field,
      operator: 'FILTER_OPERATOR_EQ',
      value: { string_value: value },
    });
    if (statusFilter !== 'all') rules.push(eq('status', statusFilter));
    if (typeFilter !== 'all') rules.push(eq('type', typeFilter));
    if (regionFilter) rules.push(eq('region', regionFilter));
    if (search) rules.push({
      field: 'name',
      operator: 'FILTER_OPERATOR_LIKE',
      value: { string_value: search },
    });

    store.ListTenants({
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
      ...(rules.length > 0
        ? { filters: { logic: 'LOGIC_OPERATOR_AND', rules, groups: [] } as any }
        : {}),
    }).catch(() => {});
  }, [searchParams, statusFilter, typeFilter, regionFilter, search]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (typeFilter !== 'all') params.type = typeFilter;
    if (regionFilter) params.region = regionFilter;
    setSearchParams(params);
  };

  const handleFilterChange = (key: string, value: string) => {
    const params: Record<string, string> = {};
    const newSearch = key === 'search' ? value : search;
    const newStatus = key === 'status' ? value : statusFilter;
    const newType = key === 'type' ? value : typeFilter;
    const newRegion = key === 'region' ? value : regionFilter;

    if (newSearch) params.search = newSearch;
    if (newStatus !== 'all') params.status = newStatus;
    if (newType !== 'all') params.type = newType;
    if (newRegion) params.region = newRegion;

    if (key === 'status') setStatusFilter(value);
    if (key === 'type') setTypeFilter(value);
    if (key === 'region') setRegionFilter(value);

    setSearchParams(params);
  };

  const handleSuspend = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to suspend "${name}"?`)) return;
    await ops.SuspendTenant(id, { reason_detail: 'Suspended by platform admin' });
    loadTenants();
  };

  const handleReactivate = async (id: string) => {
    await ops.ReactivateTenant(id, {});
    loadTenants();
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) return;
    await store.DeleteTenant(id);
    loadTenants();
  };

  const totalTenants = store.items.length;
  const activeTenants = store.items.filter(
    (t: any) => t.status === 'ACTIVE' || t.status === 'TENANT_STATUS_ACTIVE',
  ).length;
  const suspendedTenants = store.items.filter(
    (t: any) => t.status === 'SUSPENDED' || t.status === 'TENANT_STATUS_SUSPENDED',
  ).length;
  const pendingTenants = store.items.filter(
    (t: any) => t.status === 'PENDING' || t.status === 'TENANT_STATUS_PENDING',
  ).length;

  return (
    <div className="space-y-6">
      {/* Page header with stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('tenant.admin.management')}
          </h1>
          <div className="flex gap-4 mt-2">
            <span className="text-sm text-muted-foreground">
              {t('tenant.admin.total')}:{' '}
              <span className="font-semibold text-foreground">{totalTenants}</span>
            </span>
            <span className="text-sm text-muted-foreground">
              {t('tenant.admin.active')}:{' '}
              <span className="font-semibold text-green-600">{activeTenants}</span>
            </span>
            <span className="text-sm text-muted-foreground">
              {t('tenant.admin.suspended')}:{' '}
              <span className="font-semibold text-red-600">{suspendedTenants}</span>
            </span>
            <span className="text-sm text-muted-foreground">
              {t('tenant.admin.pending')}:{' '}
              <span className="font-semibold text-yellow-600">{pendingTenants}</span>
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/tenants/migrations">
            <Button variant="secondary">
              {t('tenant.admin.migrations')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('tenant.admin.filterStatus')}
              </label>
              <Select
                value={statusFilter}
                onChange={(e) => handleFilterChange('status', e.target.value)} fullWidth={false}>
                <option value="all">{t('tenant.admin.allStatuses')}</option>
                {TENANT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0) + s.slice(1).toLowerCase()}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('tenant.admin.filterType')}
              </label>
              <Select value={typeFilter} onChange={(e) => handleFilterChange('type', e.target.value)} fullWidth={false}>
                <option value="all">{t('tenant.admin.allTypes')}</option>
                {TENANT_TYPES.map((tp) => (
                  <option key={tp} value={tp}>
                    {tp.charAt(0) + tp.slice(1).toLowerCase()}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('tenant.admin.filterRegion')}
              </label>
              <Input
                placeholder={t('tenant.admin.regionPlaceholder')}
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                fullWidth={false}
                className="w-40"
              />
            </div>

            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder={t('tenant.admin.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth={false}
                className="w-full"
              />
            </div>

            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      {ops.error ? <ErrorAlert error={ops.error} dismissible onDismiss={() => ops.clearError()} /> : null}

      {/* Tenants table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tenant.admin.colName')}</TableHead>
              <TableHead>{t('tenant.admin.colSlug')}</TableHead>
              <TableHead>{t('tenant.admin.colType')}</TableHead>
              <TableHead>{t('tenant.admin.colStatus')}</TableHead>
              <TableHead>{t('tenant.admin.colOwner')}</TableHead>
              <TableHead className="text-right">
                {t('tenant.admin.colMembers')}
              </TableHead>
              <TableHead>{t('tenant.admin.colRegion')}</TableHead>
              <TableHead>{t('tenant.admin.colCreated')}</TableHead>
              <TableHead className="text-right">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty
                colSpan={9}
                message={t('tenant.admin.noTenants')}
              />
            ) : (
              store.items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                const rawStatus = normalizeStatus(String(item.status ?? ''));
                const rawType = normalizeType(String(item.type ?? ''));
                const isSuspended =
                  item.status === 'SUSPENDED' || item.status === 'TENANT_STATUS_SUSPENDED';

                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <Link
                        to={`/admin/tenants/${itemId}`}
                        className="text-sm font-medium text-foreground hover:text-primary"
                      >
                        {String(item.name ?? '')}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground font-mono">
                        {String(item.slug ?? '--')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={mapTenantTypeToVariant(item.type) as any}
                        size="sm"
                      >
                        {rawType
                          ? rawType.charAt(0) + rawType.slice(1).toLowerCase()
                          : '--'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        variant={mapTenantStatusToVariant(item.status)}
                        label={
                          rawStatus
                            ? rawStatus.charAt(0) + rawStatus.slice(1).toLowerCase()
                            : 'Unknown'
                        }
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {String(item.owner?.name || item.owner || '--')}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm text-foreground tabular-nums">
                        {item.member_count ?? item.total_members ?? '--'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {String(item.region ?? '--')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(item.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/admin/tenants/${itemId}`}>
                          <Button variant="ghost" size="sm">
                            {t('common.view')}
                          </Button>
                        </Link>
                        {isSuspended ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600"
                            onClick={() => handleReactivate(itemId)}
                          >
                            {t('tenant.admin.reactivate')}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-yellow-600"
                            onClick={() => handleSuspend(itemId, item.name)}
                          >
                            {t('tenant.admin.suspend')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleDelete(itemId, item.name)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">
          {t('tenant.admin.showing')} {store.items.length}{' '}
          {t('tenant.admin.tenantUnit')}
          {store.items.length !== 1 ? 's' : ''}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!searchParams.get('cursor')}
            onClick={() => {
              const params = Object.fromEntries(searchParams.entries());
              delete params.cursor;
              setSearchParams(params);
            }}
          >
            {t('common.first')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={store.items.length < 20}
            onClick={() => {
              const lastItem = store.items[store.items.length - 1];
              const cursor = lastItem?.entity?.id || '';
              if (cursor) {
                const params = Object.fromEntries(searchParams.entries());
                params.cursor = cursor;
                setSearchParams(params);
              }
            }}
          >
            {t('common.next')}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default AdminTenantsPage;
